// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Thumbnail.tsx
 * @input Uses React, stylex, Button, Skeleton, Spinner, MediaTheme, useImageMode
 * @output Exports Thumbnail component, ThumbnailProps
 * @position Core implementation; consumed by index.ts
 *
 * Square preview card for image attachments. Shows a skeleton shimmer while
 * the image loads, the image on success, or a placeholder on failure.
 * Uses useImageMode (APCA) to detect image luminance so the overlaid
 * remove button always has sufficient contrast.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Thumbnail/Thumbnail.doc.mjs
 * - /packages/core/src/Thumbnail/Thumbnail.test.tsx
 * - /packages/core/src/Thumbnail/index.ts
 * - /apps/storybook/stories/Thumbnail.stories.tsx
 * - /packages/cli/templates/blocks/components/Thumbnail/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars, spacingVars, shadowVars, durationVars, easeVars } from "../theme/tokens.stylex.js";
import { Button } from "../Button/index.js";
import { Icon } from "../Icon/index.js";
import { Skeleton } from "../Skeleton/index.js";
import { Spinner } from "../Spinner/index.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { MediaTheme } from "../theme/MediaTheme.js";
import { useImageMode } from "../hooks/useImageMode.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/** Sample the region behind the remove button (20px button, 4px inset, in 64px container). */
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const BUTTON_REGION = {
  x: 0.5,
  y: 0.06,
  width: 0.44,
  height: 0.44
};
// =============================================================================
// Styles
// =============================================================================

const styles = {
  root: {
    kVAEAm: "x1n2onr6",
    k1xSpc: "x3nfvp2",
    kXwgrk: "xdt5ytf",
    kzqmXN: "x1fu8urw",
    kmuXW: "x2lah0s",
    kHBbk8: "xc8icb0",
    $$css: true
  },
  removeButtonOverrides: {
    kVAEAm: "x10l6tqk",
    k87sOh: "x8d4r5l",
    kCIrl2: "xs7l08f",
    kY2c9j: "x1vjfegm",
    "--_button-radius": "xh8ntw8",
    kZKoxP: "x1qx5ct2",
    k7Eaqz: "xt4ypqs",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kfzvcC: "x47corl",
    $$css: true
  }
};

// =============================================================================
// Placeholder icon — a simple image silhouette
// =============================================================================

function ImagePlaceholder() {
  return /*#__PURE__*/_jsx("svg", {
    width: 24,
    height: 24,
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": "true",
    children: /*#__PURE__*/_jsx("path", {
      d: "M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2M8.5 13.5l2.5 3 3.5-4.5 4.5 6H5l3.5-5.5z"
    })
  });
}

// =============================================================================
// Component
// =============================================================================

/**
 * A square thumbnail preview for image attachments.
 *
 * Shows a skeleton shimmer while the image loads, the image on success, or
 * a placeholder icon on failure / when no src is provided. An overlaid
 * remove button appears when `onRemove` is set.
 *
 * Uses `useImageMode` (APCA) to detect image luminance and `MediaTheme`
 * to ensure the remove button always has sufficient contrast against the image.
 *
 * @example
 * ```
 * <Thumbnail src="/photo.jpg" alt="Vacation photo" onRemove={() => {}} />
 * <Thumbnail src="/preview.png" alt="Preview" onClick={() => {}} label="preview.png" />
 * ```
 */
export function Thumbnail({
  src,
  alt,
  label,
  onRemove,
  onClick,
  isLoading = false,
  isDisabled = false,
  xstyle,
  className,
  style,
  'data-testid': testId,
  ref,
  ...props
}) {
  const t = useTranslator();
  const imageMode = useImageMode(src, {
    region: BUTTON_REGION,
    fallback: null
  });
  const hasSrc = src != null;
  const showSkeleton = isLoading && !hasSrc;
  const showImage = hasSrc && !showSkeleton;
  const showUploadOverlay = isLoading && hasSrc;
  const showPlaceholder = !isLoading && !hasSrc;
  const isInteractive = onClick != null && !isDisabled && !isLoading;
  const accessibleName = label && alt ? `${label} — ${alt}` : label ?? alt ?? 'thumbnail';
  const imageContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [showImage && /*#__PURE__*/_jsx("img", {
      src: src,
      alt: alt ?? '',
      ...{
        className: "xh8yej3 x5yr21d xl1xv1r x1lliihq"
      }
    }), showSkeleton && /*#__PURE__*/_jsx(Skeleton, {
      radius: 2
    }), showPlaceholder && /*#__PURE__*/_jsx("div", {
      ...{
        className: "x78zum5 x6s0dn4 xl56j7k xh8yej3 x5yr21d xv9yike"
      },
      children: /*#__PURE__*/_jsx(ImagePlaceholder, {})
    })]
  });
  const removeButtonEl = onRemove != null && !isDisabled ? /*#__PURE__*/_jsx(Button, {
    icon: /*#__PURE__*/_jsx(Icon, {
      icon: "close",
      size: "xsm"
    }),
    label: t('@astryx.thumbnail.remove', {
      accessibleName
    }),
    variant: "secondary",
    size: "sm",
    isIconOnly: true,
    onClick: e => {
      e.stopPropagation();
      onRemove(e);
    },
    xstyle: styles.removeButtonOverrides
  }) : null;
  const thumbnail = /*#__PURE__*/_jsx("div", {
    ref: ref,
    "data-testid": testId,
    role: "group",
    "aria-label": accessibleName,
    ...mergeProps(themeProps('thumbnail'), stylex.props(styles.root, isDisabled && styles.disabled, xstyle), className, style),
    ...props,
    children: /*#__PURE__*/_jsxs("div", {
      ...{
        0: {
          className: "x1n2onr6 xh8yej3 x1plog1 xh6dtrn xb3r6kr x17x4s8c"
        },
        1: {
          className: "x1n2onr6 xh8yej3 x1plog1 xh6dtrn xb3r6kr x17x4s8c x1ypdohk x1kx2cm7 xuedmi6 xlr8y92 x1gnnqk1 xhouywz x1hc1fzr xzomjkg x1tmayz8 x1irc7jg x1wfwxd8 xdjuwb3"
        }
      }[!!isInteractive << 0],
      children: [isInteractive ? /*#__PURE__*/_jsx("button", {
        type: "button",
        onClick: onClick,
        "aria-label": t('@astryx.thumbnail.open', {
          accessibleName
        }),
        ...{
          className: "x1ypdohk x1lliihq xh8yej3 x5yr21d xh6dtrn xb3r6kr"
        },
        children: imageContent
      }) : imageContent, showImage && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x10l6tqk x10a8y8t x1pjcqnp x1er153n x47corl"
        }
      }), showUploadOverlay && /*#__PURE__*/_jsx("div", {
        ...{
          className: "x10l6tqk x10a8y8t x78zum5 x6s0dn4 xl56j7k x1u4601x x1pjcqnp x1vjfegm x14ju556"
        },
        children: /*#__PURE__*/_jsx(Spinner, {
          size: "sm",
          shade: "onMedia"
        })
      }), removeButtonEl != null && imageMode != null ? /*#__PURE__*/_jsx(MediaTheme, {
        mode: imageMode,
        children: removeButtonEl
      }) : removeButtonEl]
    })
  });
  if (label != null) {
    return /*#__PURE__*/_jsx(Tooltip, {
      content: label,
      children: thumbnail
    });
  }
  return thumbnail;
}
Thumbnail.displayName = 'Thumbnail';